import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import type { DataTableRowEventParams, SearchValues, TableData } from '@/components/custom/erp';
import type { DonViTinh, VatTuSanPham, VatTuSanPhamInput } from '@/types/schemas';
import { refreshTableData } from '@/hooks/queries/refresh-table-data';
import { useCRUD, useDonViTinh, useForms } from '@/hooks';
import { getItemColumns } from './cols-definition';
import { usePathname, useRouter } from '@/routing';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

interface VatTuSanPhamContextValue {
  // CRUD operations
  addItem: (item: VatTuSanPhamInput) => Promise<VatTuSanPham>;
  updateItem: (id: string, item: VatTuSanPhamInput) => Promise<VatTuSanPham>;
  deleteItem: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
  isLoading: boolean;
  data: VatTuSanPham[];
  detail: VatTuSanPham | null;
  currentPage: number;
  totalItems: number;
  handlePageChange: (page: number) => Promise<void>;

  // Search state
  searchState: {
    filterParams: SearchValues;
    searchResults: VatTuSanPham[];
    isSearching: boolean;
  };
  handleSearchChange: (values: SearchValues) => void;
  hasFilterParams: boolean;
  tables: TableData[];

  // Form state
  showForm: boolean;
  showDelete: boolean;
  selectedObj: VatTuSanPham | null;
  selectedRowIndex: string | null;
  formMode: 'add' | 'edit' | 'view';
  isCopyMode: boolean;
  handleCloseForm: () => void;
  handleCloseDelete: () => void;
  handleAddClick: () => void;
  handleEditClick: () => void;
  handleViewClick: (row?: VatTuSanPham) => void;
  handleDeleteClick: () => void;
  handleCopyClick: () => void;
  handleRowClick: (params: DataTableRowEventParams<VatTuSanPham>) => void;
  handleRefreshClick: () => void;
  handleRowDoubleClick: (params: DataTableRowEventParams<VatTuSanPham>) => void;
  handleTabChange: (tabPath: string) => void;
  clearSelection: () => void;
  showBulkDelete: boolean;
  handleCloseBulkDelete: () => void;
  isImportDialogOpen: boolean;
  setIsImportDialogOpen: (open: boolean) => void;
  handleOpenImportExcel: () => void;

  // Don vi tinh
  donViTinhs: DonViTinh[];
}

export const VatTuSanPhamContext = createContext<VatTuSanPhamContextValue | null>(null);

export function useVatTuSanPhamContext() {
  const context = useContext(VatTuSanPhamContext);
  if (!context) {
    throw new Error('useVatTuSanPhamContext must be used within VatTuSanPhamProvider');
  }
  return context;
}

export function VatTuSanPhamProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    isLoading,
    data,
    currentPage,
    totalItems,
    searchData,
    handlePageChange,
    fetchDetail,
    detail
  } = useCRUD<VatTuSanPham, VatTuSanPhamInput>({
    endpoint: QUERY_KEYS.VAT_TU,
    pageSize: 100
  });

  const { searchState, handleSearchChange, onPageChange, hasFilterParams, handleRefreshClick } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = useMemo(
    () => (hasFilterParams ? searchState.searchResults : data || []),
    [data, hasFilterParams, searchState.searchResults]
  );
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const { donViTinhs, fetchDonViTinhs } = useDonViTinh();

  useEffect(() => {
    fetchDonViTinhs();
  }, [fetchDonViTinhs]);

  const {
    showForm,
    showDelete,
    selectedObj,
    selectedRowIndex,
    formMode,
    isCopyMode,
    isImportDialogOpen,
    setIsImportDialogOpen,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    showBulkDelete,
    handleCloseBulkDelete,
    handleOpenImportExcel
  } = useForms<VatTuSanPham>(fetchDetail);

  const handleRefreshTableData = useCallback(
    async () =>
      refreshTableData({
        hasFilterParams,
        refreshFilteredData: handleRefreshClick,
        refreshListData: refreshData
      }),
    [handleRefreshClick, hasFilterParams, refreshData]
  );

  const handleRowDoubleClick = (params: DataTableRowEventParams<VatTuSanPham>) => {
    handleRowClick(params);
    handleViewClick();
  };

  const handleTabChange = (tabPath: string) => {
    if (tabPath === pathname) return;
    router.push(`/danh-muc/vat-tu-san-pham-va-kho/${tabPath}`);
  };

  const itemColumns = useMemo(() => getItemColumns(donViTinhs), [donViTinhs]);
  const tables = useMemo(
    () => [
      {
        name: 'Vật tư, sản phẩm',
        rows: displayData,
        columns: itemColumns,
        value: 0
      },
      {
        name: 'Lô hàng',
        rows: [],
        columns: [],
        value: 1
      },
      {
        name: 'Kho hàng',
        rows: [],
        columns: [],
        value: 2
      },
      {
        name: 'Vị trí kho hàng',
        rows: [],
        columns: [],
        value: 3
      }
    ],
    [displayData, itemColumns]
  );

  return (
    <VatTuSanPhamContext.Provider
      value={{
        // CRUD
        addItem,
        updateItem,
        deleteItem,
        refreshData: handleRefreshTableData,
        isLoading,
        data,
        currentPage: resolvedCurrentPage,
        totalItems: resolvedTotalItems,
        handlePageChange: onPageChange,
        detail,

        searchState,
        handleSearchChange,
        hasFilterParams,
        tables,
        showForm,
        showDelete,
        selectedObj,
        selectedRowIndex,
        formMode,
        isCopyMode,
        isImportDialogOpen,
        setIsImportDialogOpen,
        handleCloseForm,
        handleCloseDelete,
        handleAddClick,
        handleEditClick,
        handleViewClick,
        handleDeleteClick,
        handleCopyClick,
        handleRefreshClick: handleRefreshTableData,
        handleRowClick,
        handleRowDoubleClick,
        handleTabChange,
        clearSelection,
        showBulkDelete,
        handleCloseBulkDelete,
        handleOpenImportExcel,
        donViTinhs
      }}
    >
      {children}
    </VatTuSanPhamContext.Provider>
  );
}

export function VatTuSanPhamSearchProvider({ children }: { children: ReactNode }) {
  return <VatTuSanPhamProvider>{children}</VatTuSanPhamProvider>;
}

export function useVatTuSanPhamSearchContext() {
  return useVatTuSanPhamContext();
}
